import { useEffect, useRef } from "react";

import { useHoldStaleBundleReload } from "lib/staleBundleReload";

interface UseUnsavedChangeWarningOptions {
  enablePopstateWarning?: boolean;
}

export const useUnsavedChangeWarning = (
  isUnsaved: boolean,
  options: UseUnsavedChangeWarningOptions = {},
) => {
  const { enablePopstateWarning = false } = options;
  const isUnsavedRef = useRef(isUnsaved);
  // A reload would trip the browser's "Leave site?" prompt on a plain link click.
  useHoldStaleBundleReload(isUnsaved);

  useEffect(() => {
    isUnsavedRef.current = isUnsaved;
  }, [isUnsaved]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isUnsavedRef.current) {
        event.preventDefault();
        // Modern browsers ignore the custom message and show their own
        // but we still need to set returnValue for the event to work
        event.returnValue = "";
        return "";
      }
    };

    // For browser navigation (back/forward buttons), we need to intercept
    // before the navigation happens by adding a history entry
    const preventNavigation = () => {
      if (isUnsavedRef.current) {
        // Add a dummy state to prevent back navigation
        window.history.pushState({ preventNavigation: true }, "", window.location.href);
      }
    };

    const handlePopState = (event: PopStateEvent) => {
      if (isUnsavedRef.current && !event.state?.preventNavigation) {
        const confirmNavigation = window.confirm(
          "You have unsaved changes. Are you sure you want to leave this page?",
        );

        if (!confirmNavigation) {
          // Push the current state back to stay on the page
          window.history.pushState({ preventNavigation: true }, "", window.location.href);
          return;
        }
        // If user confirms, they want to navigate away, so we allow it
        // by not preventing the default behavior
      } else if (event.state?.preventNavigation) {
        // This is our dummy state, add another one to maintain the barrier
        window.history.pushState({ preventNavigation: true }, "", window.location.href);
      }
    };

    if (isUnsaved) {
      window.addEventListener("beforeunload", handleBeforeUnload);

      if (enablePopstateWarning) {
        window.addEventListener("popstate", handlePopState);
        // Set up initial navigation barrier
        preventNavigation();
      }
    } else {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);

      // Clean up navigation barrier by replacing current state
      if (enablePopstateWarning && window.history.state?.preventNavigation) {
        window.history.replaceState(null, "", window.location.href);
      }
    }

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
    };
  }, [isUnsaved, enablePopstateWarning]);
};
